import { ExternalLink, Phone, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { ConsultationWithRelations } from '@snootsvet/shared'
import { formatElapsedTime } from '@snootsvet/shared'

import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button, buttonVariants } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { RemoteConsultationForm } from '@/frontend/features/remote-consultations/remote-consultation.form'
import { useListConsultations } from '@/frontend/hooks/api/consultations/use-list-consultations'
import { usePatchConsultation } from '@/frontend/hooks/api/consultations/use-patch-consultation'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { cn } from '@/frontend/lib/utils'

type Tab = 'my' | 'queue' | 'active' | 'resolved'

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card p-4 text-center">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-muted-foreground">{label}</p>
    </div>
  )
}

function ConsultationCard({
  consultation,
  onStart,
  isStarting,
}: {
  consultation: ConsultationWithRelations
  onStart: (id: string, patientId: string) => void
  isStarting: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-5 transition-shadow hover:shadow-md',
        consultation.status === 'pending' && 'bg-warning border-warning-border',
        consultation.status === 'in_progress' && 'bg-info border-info-border',
        consultation.status === 'finalised' && 'bg-success border-success-border'
      )}
    >
      <div className="flex items-stretch justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-bold">
            <NavLink to={`/patients/${consultation.patient.id}`} className="hover:underline">
              {consultation.patient.name}
            </NavLink>{' '}
            <span className="font-normal text-muted-foreground">
              (
              <NavLink to={`/members/${consultation.patient.owner.id}`} className="hover:underline">
                {consultation.patient.owner.firstName} {consultation.patient.owner.lastName}
              </NavLink>
              )
            </span>
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-muted-foreground">
            <Phone className="h-3.5 w-3.5" />
            Sick Visit Call Request
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {/* A remote consultation has no clinic by design, so it reads in the
                viewer's own zone. */}
            <FormattedDateTime value={consultation.createdAt} clinicId={null} />
          </p>
          {consultation.reason && <p className="mt-2">{consultation.reason}</p>}
          {consultation.staff && (
            <span className="text-muted-foreground">
              Assigned to{' '}
              <b>
                {consultation.staff.firstName} {consultation.staff.lastName}
              </b>
            </span>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-3">
          {consultation.status === 'pending' && (
            <Badge variant="warning">Waiting - {formatElapsedTime(consultation.createdAt)}</Badge>
          )}
          {consultation.status === 'in_progress' && (
            <Badge variant="info">In progress - {formatElapsedTime(consultation.updatedAt)}</Badge>
          )}
          {consultation.status === 'finalised' && (
            <Badge variant="success">Resolved - {formatElapsedTime(consultation.updatedAt)}</Badge>
          )}
          <div className="mt-auto flex gap-2">
            {consultation.status === 'pending' && (
              <Button
                onClick={() => onStart(consultation.id, consultation.patient.id)}
                disabled={isStarting}
              >
                Start Consultation
              </Button>
            )}
            {consultation.status !== 'pending' && (
              <NavLink
                to={`/patients/${consultation.patient.id}#${consultation.id}`}
                className={buttonVariants({ size: 'sm' })}
              >
                View Consultation
              </NavLink>
            )}
            {consultation.intercomUrl && (
              <a
                href={consultation.intercomUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={buttonVariants({ size: 'sm', variant: 'outline' })}
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Intercom
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Dialog that hosts the remote consultation creation form. */
function AddConsultationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Remote Consultation Request</DialogTitle>
        </DialogHeader>
        <RemoteConsultationForm
          onSuccess={() => onOpenChange(false)}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

/** Remote consultation queue — for remote vets to view and claim pending call requests. */
export function RemoteConsultationsPage() {
  useDocumentTitle('Remote Consultations')
  const navigate = useNavigate()
  const { hash } = useLocation()
  const { data: authStaff } = useGetAuthenticatedStaff()

  const activeTab: Tab = (hash.slice(1) as Tab) || 'queue'
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }, [])

  const queueQuery = useListConsultations({ remote: true, status: 'pending' })
  const activeQuery = useListConsultations({ remote: true, status: 'in_progress' })
  // Always fetch today's resolved consultations so the stat card is accurate.
  const resolvedQuery = useListConsultations({
    remote: true,
    status: 'finalised',
    finalisedSince: today,
  })

  const isLoadingMap: Record<Tab, boolean> = {
    my: activeQuery.isLoading,
    queue: queueQuery.isLoading,
    active: activeQuery.isLoading,
    resolved: resolvedQuery.isLoading,
  }

  // "My Consultations" is derived from the active list filtered to the current vet
  const myConsultations = activeQuery.data?.filter((c) => c.staff?.id === authStaff?.id) ?? []

  const { mutateAsync: patchConsultation } = usePatchConsultation()
  const [startingId, setStartingId] = useState<string | null>(null)

  const handleStart = async (consultationId: string, patientId: string) => {
    setStartingId(consultationId)
    try {
      await patchConsultation({ id: consultationId, body: { action: 'claim' } })
      navigate(`/patients/${patientId}`)
    } catch {
      toast.error('Failed to start consultation')
    } finally {
      setStartingId(null)
    }
  }

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'my', label: 'My Consultations', count: myConsultations.length },
    { id: 'queue', label: 'Queue', count: queueQuery.data?.length ?? 0 },
    { id: 'active', label: 'Active', count: activeQuery.data?.length ?? 0 },
    { id: 'resolved', label: 'Resolved', count: resolvedQuery.data?.length ?? 0 },
  ]

  const tabData: Record<Tab, ConsultationWithRelations[]> = {
    my: myConsultations,
    queue: queueQuery.data ?? [],
    active: activeQuery.data ?? [],
    resolved: resolvedQuery.data ?? [],
  }

  return (
    <div>
      <AddConsultationDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-headline-lg">Remote Consultations</h1>
          <p className="text-muted-foreground mt-1">
            Call-based sick visit requests only. Chat-based work continues in Intercom.
          </p>
        </div>
        <Button size="lg" onClick={() => setAddDialogOpen(true)}>
          <Plus />
          Add Remote Request
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6 max-w-lg">
        <StatCard label="Queued" value={queueQuery.data?.length ?? 0} />
        <StatCard label="Active Calls" value={activeQuery.data?.length ?? 0} />
        <StatCard label="Resolved Today" value={resolvedQuery.data?.length ?? 0} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => navigate(`#${tab.id}`, { replace: true })}
            className={`cursor-pointer px-4 py-2 font-medium rounded-t transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4">
        {isLoadingMap[activeTab] ? (
          <>
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </>
        ) : tabData[activeTab].length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">No consultations here.</p>
        ) : (
          tabData[activeTab].map((consultation) => (
            <ConsultationCard
              key={consultation.id}
              consultation={consultation}
              onStart={handleStart}
              isStarting={startingId === consultation.id}
            />
          ))
        )}
      </div>
    </div>
  )
}
